package com.elan.edubridge.shared.domain.model

import kotlinx.serialization.Serializable

/**
 * 학원 안의 정체성.
 *
 * 보호자는 여기 없다 — 학원 멤버십을 받지 않고 [GuardianLink]로만 학생에 연결되며,
 * 알림 수신(`edubridge_users/{uid}` 하위 미러) 외에 학원 데이터 접근 경로가 없다.
 * 보호자 연락처 자체는 학생 문서의 `guardianContacts`에 정적 데이터로 남는다.
 */
@Serializable
enum class UserRole(val displayName: String) {
    STUDENT("학생"),
    TEACHER("선생님"),
    ACADEMY_OWNER("학원장"),
    SUPER_ADMIN("전체 관리자"),
}

/**
 * 능력 — 학원 안에서 "무엇을 할 수 있는가"(§2.2).
 *
 * [UserRole]이 정체성을 표현하는 것과 달리 인가 판정은 전부 이 축으로만 한다.
 * Functions가 `permissionFor(role)`로 계산해 멤버십 문서에 저장하고 rules는 그 값만 읽으므로
 * 역할이 늘어도 rules는 무수정이다. 클라이언트는 이 값을 절대 쓰지 못한다.
 *
 * 문자열 값은 `functions/`의 TS·`firestore.rules`와 반드시 동일하다: VIEWER | EDITOR | ADMIN.
 */
@Serializable
enum class Permission(val rank: Int, val displayName: String) {
    /** 자기(또는 관찰 대상)에게 허용된 것만 읽기. 학생·학부모. */
    VIEWER(0, "뷰어"),

    /** 배정 범위의 출결·공지·과제 편집. 선생님. */
    EDITOR(1, "편집자"),

    /** 학원 구성원·설정 관리. 학원장·전체 관리자. */
    ADMIN(2, "관리자"),
    ;

    /** 권한 비교는 항상 이 함수를 거친다. `==` 열거는 역할 열거와 같은 병을 낳는다. */
    fun atLeast(required: Permission): Boolean = rank >= required.rank
}

/** 만 14세 미만 판별에 필요한 최소 정보. 생년월일을 수집하는 가입 흐름에서도 기존 권한 계약은 유지한다. */
@Serializable
enum class AgeGroup { UNKNOWN, UNDER_14, OVER_14 }

/**
 * 가입·프로필에 저장하는 생년월일의 단일 형식(ISO-8601 `YYYY-MM-DD`).
 *
 * `kotlinx-datetime`을 추가하지 않은 공유 모듈에서도 같은 검증을 하도록 달력 윤년 규칙까지 직접
 * 검사한다. 저장값은 원문을 보존하지 않고 이 형식만 허용하므로 Firestore 프로필에 임의 문자열이
 * 들어가지 않는다.
 */
object BirthDate {
    private val pattern = Regex("^(\\d{4})-(\\d{2})-(\\d{2})$")

    fun normalize(raw: String): String? {
        val value = raw.trim()
        val match = pattern.matchEntire(value) ?: return null
        val year = match.groupValues[1].toInt()
        val month = match.groupValues[2].toInt()
        val day = match.groupValues[3].toInt()
        if (year == 0 || month !in 1..12) return null
        if (day !in 1..daysInMonth(year, month)) return null
        return value
    }

    /** Canonical ISO dates compare lexicographically, so no platform date API is needed here. */
    fun isOnOrBefore(raw: String, today: String): Boolean {
        val date = normalize(raw) ?: return false
        val normalizedToday = normalize(today) ?: return false
        return date <= normalizedToday
    }

    private fun daysInMonth(year: Int, month: Int): Int = when (month) {
        2 -> if (isLeapYear(year)) 29 else 28
        4, 6, 9, 11 -> 30
        else -> 31
    }

    private fun isLeapYear(year: Int): Boolean =
        year % 4 == 0 && (year % 100 != 0 || year % 400 == 0)
}

@Serializable
enum class GuardianConsentStatus {
    NOT_REQUIRED,
    PENDING_GUARDIAN_CONSENT,
    GRANTED,
    REJECTED,
}

/**
 * 신원 문서. 학원 단위 권한은 여기 두지 않고 [AcademyMembership]로 분리한다.
 * Firestore `edubridge_users/{uid}`에 대응한다.
 */
@Serializable
data class UserAccount(
    val id: String,
    val email: String,
    val name: String,
    /** 공개 가입 계정은 항상 STUDENT. 클라이언트가 바꿀 수 없다(§4). */
    val baseRole: UserRole = UserRole.STUDENT,
    /** 서버(custom claim)만 부여한다. 현재 사용 값은 SUPER_ADMIN 하나다(§3). */
    val globalRoles: Set<UserRole> = emptySet(),
    /** ISO-8601 `YYYY-MM-DD`. 기존 프로필은 마이그레이션 전 데이터라 null일 수 있다. */
    val birthDate: String? = null,
    val ageGroup: AgeGroup = AgeGroup.UNKNOWN,
    val consentStatus: GuardianConsentStatus = GuardianConsentStatus.NOT_REQUIRED,
)

@Serializable
enum class AppSection(val key: String, val displayName: String) {
    HOME("home", "홈"),
    ACADEMIES("academies", "학원"),
    CLASSES("classes", "수업"),
    STUDENTS("students", "학생"),
    ATTENDANCE("attendance", "출결"),
    ASSIGNMENTS("assignments", "과제"),
    NOTICES("notices", "공지"),
    USERS("users", "사용자"),
    PLATFORM("platform", "플랫폼"),
}

@Serializable
data class AuthResult(
    val account: UserAccount? = null,
    val errorMessage: String? = null,
) {
    val isSuccess: Boolean
        get() = account != null
}

/**
 * `edubridge-updateMyProfile`의 응답. 호출자가 아닌 다른 uid를 받지 않으므로,
 * 이 결과의 값은 언제나 현재 로그인한 계정의 프로필에만 적용된다.
 */
@Serializable
data class ProfileUpdateResult(
    val name: String? = null,
    val failure: CommandFailure? = null,
    val errorMessage: String? = null,
) {
    val isSuccess: Boolean get() = failure == null
}
